import { useEffect, useRef, useState } from 'react'
import { searchLocalities, type Locality } from '../api'
import { Field } from './bits'
import { DeployMap } from './DeployMap'

export interface Place {
  /** Set when the place came from the register. The receiver looks the name
   *  and position up from this rather than trusting what we send alongside it,
   *  so it is the only field that has to survive. */
  locality_no?: number
  name: string
  municipality?: string | null

  /** The register's own position for this locality — the centre of the
   *  licensed area. Carried so the picker can say how far the pin has been
   *  moved and offer the way back, and never sent as the deployment position:
   *  the receiver reads it from the register itself. */
  siteLat?: number
  siteLon?: number

  /** Where the unit actually went. Starts at the register's position and is
   *  whatever the operator last clicked or dragged to. */
  lat?: number
  lon?: number
}

/** Metres between two positions. Haversine on a sphere, which is wrong by
 *  about 0.3% at these latitudes and right enough to answer the only question
 *  being asked: is that pin at the farm, or a different fjord? */
function metresBetween(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return Math.round(2 * R * Math.asin(Math.sqrt(s)))
}

const fromLocality = (h: Locality): Place => ({
  locality_no: h.locality_no,
  name: h.name,
  municipality: h.municipality,
  siteLat: h.lat,
  siteLon: h.lon,
  // The registered centre is the starting position, not the final one. It is
  // a real position immediately -- so a deployment nobody refines is still
  // sited at the right farm -- and the map below exists to move it.
  lat: h.lat,
  lon: h.lon,
})

/** Where a deployment is, answered at two levels.
 *
 *  **Location, then position.** Most deployments are at a licensed farm, which
 *  is already written down in the register with an official number and a
 *  position — so picking one should be the short path. But a farm's registered
 *  position is the centre of an area several hundred metres across, and the
 *  unit hangs off one cage corner or off a mooring outside the ring. The farm
 *  says which water this is; only a pin says where the hydrophone hung.
 *
 *  Reference sites, harbours and open water have no locality number at all, so
 *  the manual path cannot be removed.
 *
 *  **One map, in one place.** Every branch below renders the same `DeployMap`
 *  from the same slot in the tree. That is load-bearing, not tidiness: this
 *  used to return an entirely separate tree once a farm was chosen, so React
 *  unmounted the map and built a new one — a fresh WebGL context back at the
 *  default view of Norway, which read as the map zooming out and losing the
 *  pin at the exact moment the farm was picked. Keeping the element in one
 *  position keeps the instance, so choosing a farm only moves the camera.
 */
export function LocalityPicker({
  value, onChange,
}: { value: Place | null; onChange: (p: Place | null) => void }) {
  const [mode, setMode] = useState<'register' | 'manual'>('register')
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Locality[]>([])
  const [stale, setStale] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [searched, setSearched] = useState(false)

  // Manual fields, kept separate from `value` so a half-typed coordinate does
  // not repeatedly re-emit a broken place to the wizard's validation.
  const [mName, setMName] = useState('')
  const [mLat, setMLat] = useState('')
  const [mLon, setMLon] = useState('')

  // Debounced, because this is typed into and the receiver's cache is only
  // cheap per request, not free. 250 ms is below the threshold where a search
  // box feels laggy and well above per-keystroke.
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (mode !== 'register') return
    const needle = q.trim()
    if (needle.length < 2) { setHits([]); setSearched(false); return }
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(async () => {
      setBusy(true)
      try {
        const r = await searchLocalities(needle, 12)
        setHits(r.items)
        setSearched(true)
        setStale(
          r.stale
            ? r.error
              ? 'The register is unreachable — showing the last list we fetched.'
              : 'This list may be out of date.'
            : null,
        )
      } catch {
        setHits([])
        setSearched(true)
        setStale('Could not reach the register.')
      } finally {
        setBusy(false)
      }
    }, 250)
    return () => window.clearTimeout(timer.current)
  }, [q, mode])

  const emitManual = (name: string, lat: string, lon: string) => {
    setMName(name); setMLat(lat); setMLon(lon)
    onChange(name.trim()
      ? {
          name: name.trim(),
          lat: lat === '' ? undefined : Number(lat),
          lon: lon === '' ? undefined : Number(lon),
        }
      : null)
  }

  const chosen = value?.locality_no ? value : null

  const centre = chosen && chosen.siteLat != null && chosen.siteLon != null
    ? { lat: chosen.siteLat, lon: chosen.siteLon } : null

  const manualPin =
    mLat !== '' && mLon !== '' && !Number.isNaN(Number(mLat)) && !Number.isNaN(Number(mLon))
      ? { lat: Number(mLat), lon: Number(mLon) } : null

  const pin = chosen
    ? (chosen.lat != null && chosen.lon != null ? { lat: chosen.lat, lon: chosen.lon } : null)
    : mode === 'manual' ? manualPin : null

  const moved = pin && centre ? metresBetween(centre, pin) : null

  // What the camera should be looking at. A chosen farm is one place; a live
  // search is a set of them, so typing a name walks the view to the coast it
  // is on. Never the pin: framing and placing are different acts, and folding
  // them together would move a position that had already been set.
  const fit = chosen
    ? (centre ? [centre] : undefined)
    : mode === 'register' ? hits.map((h) => ({ lat: h.lat, lon: h.lon })) : undefined

  // Clicking a farm circle is the same act as picking it from the list, so it
  // produces the same value. Clicking open water is not: it is a position with
  // no locality number, which is what a reference site or a mooring between
  // farms actually is — so it drops into the manual path carrying the click.
  const pick = (p: { lat: number; lon: number }) => {
    if (chosen) { onChange({ ...chosen, lat: p.lat, lon: p.lon }); return }
    if (mode !== 'manual') setMode('manual')
    emitManual(mName, p.lat.toFixed(5), p.lon.toFixed(5))
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      {chosen ? (
        <div className="card" style={{ padding: 16 }}>
          <div className="label">Location</div>
          <div className="display" style={{ fontSize: 19, margin: '4px 0 2px' }}>
            {chosen.name}
          </div>
          <div className="faint">
            {chosen.municipality} · locality <span className="num">{chosen.locality_no}</span>
          </div>
          <button className="btn ghost sm" style={{ marginTop: 12 }}
            onClick={() => { onChange(null); setQ(''); setHits([]); setSearched(false) }}>
            Choose somewhere else
          </button>
        </div>
      ) : (
        <div className="seg" role="tablist" aria-label="How to choose the place">
          <button type="button" role="tab" aria-selected={mode === 'register'}
            className={mode === 'register' ? 'on' : undefined}
            onClick={() => { setMode('register'); onChange(null) }}>
            From the register
          </button>
          <button type="button" role="tab" aria-selected={mode === 'manual'}
            className={mode === 'manual' ? 'on' : undefined}
            onClick={() => { setMode('manual'); onChange(null) }}>
            Somewhere else
          </button>
        </div>
      )}

      {!chosen && mode === 'register' && (
        <>
          <Field label="Find the farm" hint="Search by name, municipality, or locality number. Accents are optional — ljosoy finds Ljøsøy.">
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
              placeholder="Ljøsøy, ØYGARDEN, or 14435" />
          </Field>

          {stale && <div className="notice">{stale}</div>}
          {busy && <div className="faint">Searching…</div>}

          {!busy && searched && hits.length === 0 && (
            <div className="notice">
              Nothing matched. If this place is not a licensed farm, use
              “Somewhere else”.
            </div>
          )}

          {hits.length > 0 && (
            <ul className="picklist">
              {hits.map((h) => (
                <li key={h.locality_no}>
                  <button type="button" onClick={() => onChange(fromLocality(h))}>
                    <span className="picklist-name">{h.name}</span>
                    <span className="faint">
                      {h.municipality} · <span className="num">{h.locality_no}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {!chosen && mode === 'manual' && (
        <>
          <Field label="Place name" hint="What your team calls it. A new site is created if this name is new.">
            <input value={mName} autoFocus
              onChange={(e) => emitManual(e.target.value, mLat, mLon)}
              placeholder="Bergen Nord" />
          </Field>
          {/* The same map, now driven by the typed coordinates and driving them
            * back. Two views of one fact, so a number typed wrong is visible as
            * a pin in the wrong fjord rather than as a number that looks like
            * all other numbers. */}
          <div className="grid c2">
            <Field label="Latitude">
              <input type="number" step="0.00001" value={mLat}
                onChange={(e) => emitManual(mName, e.target.value, mLon)}
                placeholder="60.39299" />
            </Field>
            <Field label="Longitude">
              <input type="number" step="0.00001" value={mLon}
                onChange={(e) => emitManual(mName, mLat, e.target.value)}
                placeholder="5.32415" />
            </Field>
          </div>
        </>
      )}

      <div>
        <div className="label">Position</div>
        <p className="lead" style={{ margin: '2px 0 0' }}>
          {chosen
            ? "Click where the unit actually hung, or drag the marker. It starts at the farm's registered centre."
            : mode === 'manual'
              ? 'Click the map to set the position, or type it above.'
              : 'Search above and pick the farm, or click open water for somewhere the register does not list.'}
        </p>
      </div>

      {/* One instance, one slot, every branch. See the note on the component. */}
      <DeployMap
        farms={hits}
        pin={pin}
        fit={fit}
        onPick={pick}
        onPickFarm={(h) => onChange(fromLocality(h))}
      />

      {chosen && (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="faint">
            {pin
              ? <><span className="num">{pin.lat.toFixed(5)}, {pin.lon.toFixed(5)}</span>
                  {moved != null && (moved === 0
                    ? ' · at the registered centre'
                    : <> · <span className="num">{moved}</span> m from the registered centre</>)}</>
              : 'No position set.'}
          </span>
          {moved != null && moved > 0 && (
            <button className="btn ghost sm"
              onClick={() => onChange({ ...chosen, lat: chosen.siteLat, lon: chosen.siteLon })}>
              Reset to centre
            </button>
          )}
        </div>
      )}

      {/* A pin a long way from the farm is more likely to be a misclick than a
        * mooring, and it is cheap to say so now rather than discover it when
        * the recordings are attributed. Not blocking: a reference mooring
        * genuinely can sit a kilometre off. */}
      {chosen && moved != null && moved > 2000 && (
        <div className="notice">
          That is <span className="num">{(moved / 1000).toFixed(1)}</span> km from
          {' '}{chosen.name}. Correct if the unit was on a distant mooring,
          worth a second look otherwise.
        </div>
      )}

      {!chosen && mode === 'manual' && (
        <div className="notice">
          No locality number, so this site will not link to the register’s
          cohort and vessel data. That is expected for reference sites and
          harbours.
        </div>
      )}
    </div>
  )
}
